import { useState } from "react"
import { createPortal } from "react-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { useCookieConsent, CookiePreferences } from "@/context/cookie-consent-context"
import { useTranslation } from "@/hooks/use-translation"
import { Cookie, X, ChevronDown, User, Database } from "lucide-react"

export function CookieConsentBanner() {
  const { showBanner, preferences, acceptAll, savePreferences } = useCookieConsent()
  const { t } = useTranslation()
  const [showCustomize, setShowCustomize] = useState(false)
  const [showLearnMore, setShowLearnMore] = useState(false)
  const [customPreferences, setCustomPreferences] = useState<CookiePreferences>(preferences)

  if (!showBanner) return null

  const handleCustomize = () => {
    setShowCustomize(true)
    setCustomPreferences(preferences)
  }

  const handleSaveCustom = () => {
    savePreferences(customPreferences)
    setShowCustomize(false)
  }

  const handleToggle = (type: keyof CookiePreferences) => {
    if (type === 'necessary') return // Can't disable necessary cookies
    setCustomPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }))
  }

  // Customize modal using portal for safer DOM handling
  const customizeModal = showCustomize ? createPortal(
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-modal-title"
    >
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="pb-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-muted rounded-lg">
                <Cookie className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <CardTitle id="cookie-modal-title" className="text-xl">{t("cookies.title")}</CardTitle>
                <CardDescription className="text-base mt-1">
                  {t("cookies.message")}
                </CardDescription>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowCustomize(false)}
              aria-label="Close modal"
              className="h-8 w-8 flex-shrink-0"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 flex-1 min-h-0">
          {/* Necessary Cookies */}
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border">
            <div className="flex items-center gap-3 flex-1">
              <Cookie className="h-5 w-5 text-muted-foreground" />
              <div>
                <Label className="text-base font-semibold">{t("cookies.necessary")}</Label>
                <p className="text-sm text-muted-foreground">
                  {t("cookies.necessaryDesc")}
                </p>
              </div>
            </div>
            <Switch
              checked={true}
              disabled={true}
              aria-label={t("cookies.necessary")}
            />
          </div>

          {/* Analytics Cookies */}
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border">
            <div className="flex items-center gap-3 flex-1">
              <User className="h-5 w-5 text-muted-foreground" />
              <div>
                <Label className="text-base font-semibold">{t("cookies.analytics")}</Label>
                <p className="text-sm text-muted-foreground">
                  {t("cookies.analyticsDesc")}
                </p>
              </div>
            </div>
            <Switch
              checked={customPreferences.analytics}
              onCheckedChange={() => handleToggle('analytics')}
              aria-label={t("cookies.analytics")}
            />
          </div>

          {/* Advertising Cookies */}
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border">
            <div className="flex items-center gap-3 flex-1">
              <Database className="h-5 w-5 text-muted-foreground" />
              <div>
                <Label className="text-base font-semibold">{t("cookies.advertising")}</Label>
                <p className="text-sm text-muted-foreground">
                  {t("cookies.advertisingDesc")}
                </p>
              </div>
            </div>
            <Switch
              checked={customPreferences.advertising}
              onCheckedChange={() => handleToggle('advertising')}
              aria-label={t("cookies.advertising")}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4 flex-shrink-0">
            <Button
              onClick={handleSaveCustom}
              className="flex-1 h-16 text-base py-6"
              size="lg"
            >
              {t("cookies.save")}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowCustomize(false)}
              className="flex-1 h-16 text-base py-6"
              size="lg"
            >
              {t("navigation.back")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>,
    document.body
  ) : null

  return (
    <>
      {customizeModal}
      {/* Clean Cookie Consent Banner - Responsive with scroll on mobile */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <Card className="w-full max-w-xl max-h-[95vh] flex flex-col shadow-2xl">
          <CardHeader className="text-center pb-4 flex-shrink-0">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-2xl">
                  {t("app.title").charAt(0)}
                </span>
              </div>
            </div>
            <CardTitle className="text-xl font-normal mb-2">
              <span className="font-bold">{t("app.title")}</span> {t("cookies.askFor")}
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-4 flex-1 min-h-0 overflow-y-auto">
            {/* Cookie purposes list */}
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3">
                <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <User className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium leading-relaxed">
                    {t("cookies.purposeAnalytics")}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3">
                <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Database className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium leading-relaxed">
                    {t("cookies.purposeStorage")}
                  </p>
                </div>
              </div>
            </div>

            {/* Learn more section */}
            <div className="border-t pt-4">
              <Button
                variant="ghost"
                onClick={() => setShowLearnMore(!showLearnMore)}
                className="w-full justify-start p-0 h-auto text-sm font-normal"
              >
                <ChevronDown className={`h-4 w-4 mr-2 transition-transform ${showLearnMore ? 'rotate-180' : ''}`} />
                {t("cookies.learnMore")}
              </Button>

              {showLearnMore && (
                <div className="mt-3 p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground leading-relaxed">
                  <p className="mb-2">{t("cookies.learnMoreText")}</p>
                  <p>{t("cookies.learnMoreManage")}</p>
                </div>
              )}
            </div>
          </CardContent>

          {/* Action buttons - Fixed at bottom */}
          <div className="p-6 pt-0 flex-shrink-0">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Consent button first on mobile */}
              <Button
                onClick={acceptAll}
                size="lg"
                className="flex-1 h-16 text-lg font-medium px-8 py-4 order-1 sm:order-2"
              >
                {t("cookies.consent")}
              </Button>

              {/* Manage options button second on mobile */}
              <Button
                onClick={handleCustomize}
                variant="outline"
                size="lg"
                className="flex-1 h-16 text-lg font-medium px-8 py-4 order-2 sm:order-1"
              >
                {t("cookies.manageOptions")}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </>
  )
} 